import React, { useState, useEffect } from "react";
import GeneralTransactionForm from "../components/transactions/GeneralTransactionForm";

interface ExpenseCategory {
  id: number;
  name: string;
  description?: string;
  transactions_count: number;
}

interface GeneralTransaction {
  id: number;
  type: "income" | "expense";
  category?: ExpenseCategory;
  description: string;
  amount: number;
  payment_method: "cash" | "card" | "bank_transfer" | "check";
  reference_number?: string;
  vendor_name?: string;
  receipt_number?: string;
  transaction_date: string;
  processed_by: {
    id: number;
    first_name: string;
    last_name: string;
  };
  notes?: string;
  is_recurring: boolean;
  recurring_frequency?: string;
}

const GeneralTransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<GeneralTransaction[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [editingTransaction, setEditingTransaction] =
    useState<GeneralTransaction | null>(null);

  // Filters
  const [filters, setFilters] = useState({
    type: "",
    category_id: "",
    date_from: "",
    date_to: "",
  });

  // Summary data
  const [summary, setSummary] = useState({
    total_income: 0,
    total_expenses: 0,
    net_profit: 0,
    categories_breakdown: [],
  });

  const colorTheme = {
    primary: "#667eea",
    success: "#10b981",
    error: "#ef4444",
    warning: "#f59e0b",
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray500: "#6b7280",
    gray600: "#4b5563",
    gray900: "#1f2937",
    cardBg: "#ffffff",
  };

  const API_BASE =
    process.env.REACT_APP_API_URL ||
    "https://dr-bassam-elayoubi.caduceuslb.com/api";

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      // Build query parameters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const [transactionsRes, categoriesRes, summaryRes] = await Promise.all([
        fetch(`${API_BASE}/transactions?${queryParams}`, { headers }),
        fetch(`${API_BASE}/transactions/categories/list`, { headers }),
        fetch(`${API_BASE}/transactions/summary/report?${queryParams}`, {
          headers,
        }),
      ]);

      const [transactionsData, categoriesData, summaryData] = await Promise.all(
        [transactionsRes.json(), categoriesRes.json(), summaryRes.json()]
      );

      setTransactions(transactionsData.data || transactionsData);
      setCategories(categoriesData);
      setSummary(summaryData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case "cash":
        return "💵";
      case "card":
        return "💳";
      case "bank_transfer":
        return "🏦";
      case "check":
        return "📄";
      default:
        return "💰";
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: colorTheme.gray50,
        }}
      >
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            padding: "32px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: `4px solid ${colorTheme.gray200}`,
              borderTop: `4px solid ${colorTheme.primary}`,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px auto",
            }}
          />
          <p>Loading Transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: colorTheme.gray50,
        padding: "32px 24px",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            padding: "32px",
            marginBottom: "32px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: colorTheme.gray900,
                  margin: "0 0 8px 0",
                }}
              >
                💰 Financial Transactions
              </h1>
              <p
                style={{
                  color: colorTheme.gray500,
                  fontSize: "16px",
                  margin: 0,
                }}
              >
                Track income and expenses outside of medical services
              </p>
            </div>

            <button
              onClick={() => setShowTransactionForm(true)}
              style={{
                padding: "12px 24px",
                background: `linear-gradient(135deg, ${colorTheme.success} 0%, #059669 100%)`,
                color: "white",
                border: "none",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
            >
              <span>➕</span>
              Add Transaction
            </button>
          </div>

          {/* Financial Summary */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                background: `linear-gradient(135deg, ${colorTheme.success}15 0%, ${colorTheme.success}25 100%)`,
                padding: "20px",
                borderRadius: "12px",
                border: `1px solid ${colorTheme.success}30`,
              }}
            >
              <div style={{ fontSize: "24px", marginBottom: "8px" }}>📈</div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: colorTheme.success,
                  marginBottom: "4px",
                }}
              >
                {formatCurrency(summary.total_income)}
              </div>
              <div style={{ fontSize: "14px", color: colorTheme.gray600 }}>
                Total Income
              </div>
            </div>

            <div
              style={{
                background: `linear-gradient(135deg, ${colorTheme.error}15 0%, ${colorTheme.error}25 100%)`,
                padding: "20px",
                borderRadius: "12px",
                border: `1px solid ${colorTheme.error}30`,
              }}
            >
              <div style={{ fontSize: "24px", marginBottom: "8px" }}>📉</div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: colorTheme.error,
                  marginBottom: "4px",
                }}
              >
                {formatCurrency(summary.total_expenses)}
              </div>
              <div style={{ fontSize: "14px", color: colorTheme.gray600 }}>
                Total Expenses
              </div>
            </div>

            <div
              style={{
                background: `linear-gradient(135deg, ${colorTheme.primary}15 0%, ${colorTheme.primary}25 100%)`,
                padding: "20px",
                borderRadius: "12px",
                border: `1px solid ${colorTheme.primary}30`,
              }}
            >
              <div style={{ fontSize: "24px", marginBottom: "8px" }}>💎</div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color:
                    summary.net_profit >= 0
                      ? colorTheme.success
                      : colorTheme.error,
                  marginBottom: "4px",
                }}
              >
                {formatCurrency(summary.net_profit)}
              </div>
              <div style={{ fontSize: "14px", color: colorTheme.gray600 }}>
                Net Profit/Loss
              </div>
            </div>
          </div>

          {/* Filters */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
            }}
          >
            {/* Type Filter */}
            <div>
              <label
                htmlFor="filter_type"
                style={{
                  display: "block",
                  fontSize: "13px",
                  marginBottom: "6px",
                }}
              >
                Transaction Type
              </label>
              <select
                id="filter_type"
                name="filter_type"
                value={filters.type}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, type: e.target.value }))
                }
                style={{
                  padding: "10px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">All Types</option>
                <option value="income">Income Only</option>
                <option value="expense">Expenses Only</option>
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label
                htmlFor="filter_category"
                style={{
                  display: "block",
                  fontSize: "13px",
                  marginBottom: "6px",
                }}
              >
                Category
              </label>
              <select
                id="filter_category"
                name="filter_category"
                value={filters.category_id}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    category_id: e.target.value,
                  }))
                }
                style={{
                  padding: "10px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div>
              <label
                htmlFor="date_from"
                style={{
                  display: "block",
                  fontSize: "13px",
                  marginBottom: "6px",
                }}
              >
                From Date
              </label>
              <input
                type="date"
                id="date_from"
                name="date_from"
                value={filters.date_from}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, date_from: e.target.value }))
                }
                style={{
                  padding: "10px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>

            {/* To Date */}
            <div>
              <label
                htmlFor="date_to"
                style={{
                  display: "block",
                  fontSize: "13px",
                  marginBottom: "6px",
                }}
              >
                To Date
              </label>
              <input
                type="date"
                id="date_to"
                name="date_to"
                value={filters.date_to}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, date_to: e.target.value }))
                }
                style={{
                  padding: "10px",
                  border: `2px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: colorTheme.gray900,
              marginBottom: "24px",
            }}
          >
            Recent Transactions
          </h2>

          {transactions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "64px 32px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>💰</div>
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  marginBottom: "8px",
                }}
              >
                No Transactions Yet
              </h3>
              <p style={{ color: colorTheme.gray500, marginBottom: "24px" }}>
                Start by recording your first income or expense
              </p>
              <button
                onClick={() => setShowTransactionForm(true)}
                style={{
                  padding: "12px 24px",
                  background: `linear-gradient(135deg, ${colorTheme.success} 0%, #059669 100%)`,
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Add First Transaction
              </button>
            </div>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  style={{
                    padding: "20px",
                    border: `1px solid ${colorTheme.gray200}`,
                    borderRadius: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colorTheme.gray50;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  onClick={() => {
                    setEditingTransaction(transaction);
                    setShowTransactionForm(true);
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        background:
                          transaction.type === "income"
                            ? `${colorTheme.success}20`
                            : `${colorTheme.error}20`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px",
                      }}
                    >
                      {transaction.type === "income" ? "📈" : "📉"}
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: colorTheme.gray900,
                          marginBottom: "4px",
                        }}
                      >
                        {transaction.description}
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: colorTheme.gray500,
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <span>{transaction.transaction_date}</span>
                        {transaction.category && (
                          <span
                            style={{
                              padding: "2px 6px",
                              background: colorTheme.gray100,
                              borderRadius: "4px",
                              fontSize: "12px",
                            }}
                          >
                            {transaction.category.name}
                          </span>
                        )}
                        <span>
                          {getPaymentMethodIcon(transaction.payment_method)}{" "}
                          {transaction.payment_method}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color:
                          transaction.type === "income"
                            ? colorTheme.success
                            : colorTheme.error,
                        marginBottom: "4px",
                      }}
                    >
                      {transaction.type === "income" ? "+" : "-"}
                      {formatCurrency(transaction.amount)}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: colorTheme.gray500,
                      }}
                    >
                      by {transaction.processed_by.first_name}{" "}
                      {transaction.processed_by.last_name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Transaction Form Modal */}
      {showTransactionForm && (
        <GeneralTransactionForm
          isOpen={showTransactionForm}
          onClose={() => {
            setShowTransactionForm(false);
            setEditingTransaction(null);
          }}
          onSave={fetchData}
          editingTransaction={editingTransaction}
        />
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default GeneralTransactionsPage;
